import React, {useEffect, useState} from "react";
import {withRouter} from "react-router";
import {DemandValidation} from 'constants/DemandValidation';
import {connect} from "react-redux";
import {makeStyles, Typography} from "@material-ui/core";
import useSearch, {getPaginationConfig} from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import Loading from "components/Loading";
import OfferEvaluationService from "services/OfferEvaluationService";
import GridBlock from "components/GridBlock";
import Button from "components/Button";
import ViewMode from "./constants/OfferEvaluationViewMode";
import OffersEvaluationTable from "./components/OffersEvaluationTable";
import DottedPagination from "components/DottedPagination";
import Tr from "translations/Translations";
import PaginationButton, {Types as PaginationButtonTypes} from "components/PaginationButton";
import BoldTypography from "components/BoldTypography";
import DemandValidationMessages from "components/DemandValidationMessages";
import OfferEvaluationSearch from "./components/OfferEvaluationSearch";
import {OfferEvaluationRoundType} from "./constants/OfferEvaluationRoundType";
import OfferEvaluationOptionDialog from "./components/OfferEvaluationOptionDialog";


const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    italic: {
        fontStyle: 'italic',
        userSelect: 'none'
    },
    center: {
        textAlign: 'center',
        fontWeight: theme.typography.fontWeightBold,
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    name: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    leftSpacing: {
        marginLeft: theme.spacing(2)
    }
}));


function OffersEvaluation(
    {
        match,
        offerEvaluationList,
        demandValidation
    }) {

    const classes = useStyles();

    const curDemandValidation = DemandValidation.OFFERS_EVALUATION;
    const demandId = match.params.id;
    const section = demandValidation.find(x => x.section === curDemandValidation);
    const editable = section ? section.editable : false;

    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig} =
        useSearch({pagination: getPaginationConfig(1, 1), search: {roundType: OfferEvaluationRoundType.BASE}});
    const [offerLoading, setOfferLoading] = useState(true);
    const [mode, setMode] = useState(ViewMode.POSITION);
    const [optionDialog, setOptionDialog] = useState(false);

    useEffect(() => {
        return () => {
            OfferEvaluationService.clearResources(OfferEvaluationService.OFFER_EVALUATION_LIST_RESOURCE_ID);
        }
    }, [demandId])

    useEffect(() => {
        setOfferLoading(true);
        const pagination = searchData.pagination;
        const search = {...searchData.search};
        const roundType = search.roundType;
        delete search.roundType;
        OfferEvaluationService.getOffers(demandId, pagination, search, mode, roundType).then(() => (setOfferLoading(false)));
        // eslint-disable-next-line
    }, [searchData, demandId])

    useDidUpdateEffect(() => {
        if (offerEvaluationList?.totalCount !== undefined) {
            setTotalCount(offerEvaluationList.totalCount)
        }
    }, [offerEvaluationList?.totalCount]);

    const updateStatus = async (status, row, offerItem, mode) => {
        try {
            const changeType = await OfferEvaluationService.updateStatus(demandId, mode, searchData.search.roundType, offerItem, row, status);
            if (changeType === OfferEvaluationService.AVAILABLE_CHANGE_TYPES.ALL) {
                setPaginationConfig({...searchData.pagination});
            }
        } catch (e) {
            console.error(e);
        }
    }
    const updateScore = async (score, criterion, row, offerItem, mode) => {
        try {
            const changeType = await OfferEvaluationService.updateScore(demandId, mode, searchData.search.roundType, offerItem, row, criterion, score)
            if (changeType === OfferEvaluationService.AVAILABLE_CHANGE_TYPES.ALL) {
                setPaginationConfig({...searchData.pagination});
            }
        } catch (e) {
            console.error(e);
        }
    }

    const doSearch = (searchOb, paginationClear) => {
        setSearchConfig(searchOb, !paginationClear);
    };

    const onOptionChange = () => {
        setOptionDialog(false);
        setPaginationConfig({...searchData.pagination});
    }

    if (!offerEvaluationList) return <Loading/>;

    return (
        <div className={classes.root}>
            <DemandValidationMessages editable={editable} section={section}/>
            <GridBlock justify='space-between'>
                <OfferEvaluationSearch
                    doSearch={doSearch}
                    viewMode={mode}
                    searchInProgress={offerLoading}
                />
                <div>
                    <Button
                        disabled={offerLoading}
                        onClick={() => setOptionDialog(true)}
                    >
                        {Tr("show_option_dialog")}
                    </Button>
                    <Button
                        className={classes.leftSpacing}
                        disabled={offerLoading}
                        onClick={() => setMode(x => x === ViewMode.SUPPLIER ? ViewMode.POSITION : ViewMode.SUPPLIER)}
                    >
                        {mode === ViewMode.POSITION ? Tr("switch_view_per_supplier") : Tr("switch_view_per_part")}
                    </Button>
                </div>
            </GridBlock>
            {!offerEvaluationList.items[0] ?
                <BoldTypography align='center' className={classes.name} variant="h6">{Tr('no_options')}</BoldTypography>
                :
                <>
                    <GridBlock justify='space-between' className={classes.name}>
                        <PaginationButton type={PaginationButtonTypes.PREV} config={searchData.pagination} setConfig={setPaginationConfig}
                                          totalCount={totalCount}
                                          disabled={offerLoading}/>
                        <Typography className={classes.center} variant="h6">
                        <span
                            className={classes.italic}>{Tr(`offer_evaluation_mode_title_${mode.toLowerCase()}`, {value: offerEvaluationList.items[0].order})} - </span>
                            <span>{offerEvaluationList.items[0].name}</span>
                        </Typography>
                        <PaginationButton type={PaginationButtonTypes.NEXT} config={searchData.pagination} setConfig={setPaginationConfig}
                                          totalCount={totalCount}
                                          disabled={offerLoading}/>
                    </GridBlock>

                    <OffersEvaluationTable
                        offerEvaluationItem={offerEvaluationList.items[0]}
                        demandId={demandId}
                        editable={editable && !offerLoading}
                        updateScore={updateScore}
                        updateStatus={updateStatus}
                    />
                    <DottedPagination config={searchData.pagination} setConfig={setPaginationConfig} totalCount={totalCount} disabled={offerLoading}/>
                </>}

            {optionDialog && <OfferEvaluationOptionDialog demandId={demandId} onClose={() => onOptionChange()} />}
        </div>
    )
}

export default connect((state) => ({
    offerEvaluationList: state.resources[OfferEvaluationService.OFFER_EVALUATION_LIST_RESOURCE_ID],
    demandValidation: state.resources.demandValidation,
}), {})(withRouter(OffersEvaluation));

